import React, { useState, useEffect } from 'react';
import { slugify } from '@lnr-v2/utils';
import type { ClientFormProps, ClientFormData } from './ClientForm.types';
import { ImageUploader } from '../../molecules/ImageUploader';
import { useIndustries } from '../../../hooks/useIndustries';
import styles from './ClientForm.module.css';

export const ClientForm: React.FC<ClientFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const { industries, loading: industriesLoading } = useIndustries();

  const [formData, setFormData] = useState<ClientFormData>({
    name: initialData?.name || '',
    slug: initialData?.slug || '',
    logo: initialData?.logo || '',
    description: initialData?.description || '',
    isActive: initialData?.isActive ?? true,
    // Keep other fields with defaults for compatibility
    website: '',
    industry: '',
    coverDescription: '',
    tags: [],
    heroImage: '',
    brandColor: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    country: '',
    seoTitle: '',
    seoDescription: '',
    ogImage: '',
    isFeatured: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);

  // Sync form data when initialData changes (for editing)
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        name: initialData.name || '',
        slug: initialData.slug || '',
        logo: initialData.logo || '',
        description: initialData.description || '',
        industry: initialData.industry || '',
        isActive: initialData.isActive ?? true,
      }));
    }
  }, [initialData]);

  const handleLogoChange = (url: string | null) => {
    setFormData((prev) => ({ ...prev, logo: url || '' }));
    if (errors.logo) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.logo;
        return newErrors;
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        // Auto-generate slug from name when creating new client
        if (name === 'name' && !initialData) {
          updated.slug = slugify(value);
        }
        return updated;
      });
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation - only name is required
    if (!formData.name.trim()) {
      setErrors({ name: 'Le nom est requis' });
      return;
    }

    if (formData.name.trim().length < 2) {
      setErrors({ name: 'Le nom doit contenir au moins 2 caractères' });
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.container}>
      <div className={styles.tabContent}>
        <div className={styles.fieldGrid}>
          {/* Name - Required */}
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <label htmlFor="name" className={styles.label}>
              Nom <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Acme Corporation"
              className={`${styles.input} ${errors.name ? styles.error : ''}`}
              autoFocus
            />
            {errors.name && (
              <p className={styles.errorMessage}>{errors.name}</p>
            )}
            {formData.slug && (
              <p className={styles.helpText}>slug: {formData.slug}</p>
            )}
          </div>

          {/* Logo - Optional */}
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <ImageUploader
              value={formData.logo || null}
              onChange={handleLogoChange}
              onUploadStart={() => setUploading(true)}
              onUploadComplete={() => setUploading(false)}
              onUploadError={(error) => {
                setUploading(false);
                setErrors((prev) => ({ ...prev, logo: error }));
              }}
              folder="clients"
              maxSize={5}
              disabled={isSubmitting || uploading}
              label="Logo"
              helpText="PNG, JPG, SVG up to 5MB"
            />
            {errors.logo && (
              <p className={styles.errorMessage}>{errors.logo}</p>
            )}
          </div>

          {/* Description - Optional */}
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <label htmlFor="description" className={styles.label}>
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brève description du client"
              rows={3}
              className={styles.textarea}
            />
          </div>

          {/* Industry - Optional */}
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <label htmlFor="industry" className={styles.label}>
              Industrie
            </label>
            <select
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className={styles.select}
              disabled={industriesLoading}
            >
              <option value="">
                {industriesLoading
                  ? 'Chargement...'
                  : 'Sélectionner une industrie'}
              </option>
              {industries.map((industry) => (
                <option key={industry.id} value={industry.nameEN}>
                  {industry.nameEN}
                </option>
              ))}
            </select>
          </div>

          {/* Status - Checkbox */}
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <div className={styles.checkboxField}>
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className={styles.checkbox}
              />
              <label htmlFor="isActive" className={styles.checkboxLabel}>
                <span className={styles.checkboxLabelText}>Actif</span>
                <span className={styles.checkboxDescription}>
                  Le client est visible dans les listes
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className={styles.actions}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting || uploading}
          className={`${styles.button} ${styles.buttonCancel}`}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting || uploading}
          className={`${styles.button} ${styles.buttonSubmit}`}
        >
          {uploading
            ? 'Upload en cours...'
            : isSubmitting
              ? 'Enregistrement...'
              : initialData
                ? 'Mettre à jour'
                : 'Créer'}
        </button>
      </div>
    </form>
  );
};
